import { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { format } from 'date-fns';
import { updateAdditionalDetails } from "../../../API/litigationApis";
import { showToast } from "../../../utils/handleToast";

const formatDDMMYYYYToDateInput = (dateString) => {
    if (!dateString) {
        return "";
    }

    // Check if dateString is already in YYYY-MM-DD format (possibly with time)
    if (dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
        return dateString.substring(0, 10);
    }

    // Check if it is in DD-MM-YYYY format
    const parts = dateString.split('-');
    if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }

    return ""; // Return empty for unrecognized formats
};

const createEmptyParty = () => ({
    name: '',
    address: '',
    email: '',
    mobile: '', // Maps to contact_number in API payload
    pan: '',    // Maps to pan_number in API payload
});

const createEmptyLan = () => ({
    lan_number: '',
    loan_amount: '',
    claim_amount: '',
    roi_percentage: '',
    npa_date: '',
    sanction_date: '',
    agreement_date: '',
    application_date: '',
    product_name: '',
    outstanding_date_as_on: '',
});

// Keeping this function as it was, but removing its usage in useEffect
const getMaxPartiesCount = (case_item, prefix) => {
    let count = 0;
    for (let i = 1; i <= 10; i++) {
        if (case_item[`${prefix}${i}_name`] || case_item[`${prefix}${i}_address`]) {
            count = i;
        }
    }
    if (prefix === 'guarantor' && case_item.guarantor_name && count < 1) return 1;
    if (prefix === 'proprietor' && case_item.proprietor_name && count < 1) return 1;

    return count;
}

const formatDateForAPI = (dateString) => {
    if (!dateString) {
        return null;
    }
    try {
        return format(new Date(dateString), 'yyyy-MM-dd');
    } catch (error) {
        return null;
    }
};

export default function UpdateAdditionalDetailsModal({ show, onHide, selectedCase, onSuccess }) {
    const [formData, setFormData] = useState({});
    const [coBorrowerCount, setCoBorrowerCount] = useState(1);
    const [guarantorCount, setGuarantorCount] = useState(1);
    const [partnerCount, setPartnerCount] = useState(1);
    const [coBorrowers, setCoBorrowers] = useState([createEmptyParty()]);
    const [guarantors, setGuarantors] = useState([createEmptyParty()]);
    const [partners, setPartners] = useState([createEmptyParty()]);
    const [lanCount, setLanCount] = useState(1);
    const [additionalLans, setAdditionalLans] = useState([createEmptyLan()]);

    useEffect(() => {
        if (selectedCase) {
            
            const allAdditionalBorrowers = selectedCase.additional_borrowers || [];
            
            const initialCoBorrowers = [];
            const initialGuarantors = [];
            const initialPartners = [];

            // 1. Separate borrowers by type from the new API array
            allAdditionalBorrowers.forEach(party => {
                const partyData = {
                    name: party.name || '',
                    address: party.address || '',
                    email: party.email || '',
                    mobile: party.contact_number || '', 
                    pan: party.pan_number || '', 
                };

                // Normalizing borrower_type (assuming "guarantor", "coborrower", "partner" or "proprietor")
                const type = (party.borrower_type || '').toLowerCase(); 

                if (type === 'guarantor') {
                    initialGuarantors.push(partyData);
                } else if (type === 'coborrower') {
                    initialCoBorrowers.push(partyData);
                } else if (type === 'partner' || type === 'proprietor') { 
                    initialPartners.push(partyData);
                }
            });
            
            // 2. Fallback to ensure at least one empty form field is shown if none are received
            const finalCoBorrowers = initialCoBorrowers.length > 0 ? initialCoBorrowers : [createEmptyParty()];
            const finalGuarantors = initialGuarantors.length > 0 ? initialGuarantors : [createEmptyParty()];
            const finalPartners = initialPartners.length > 0 ? initialPartners : [createEmptyParty()];
            
            // 3. Set state based on parsed data
            setCoBorrowerCount(finalCoBorrowers.length);
            setGuarantorCount(finalGuarantors.length);
            setPartnerCount(finalPartners.length);

            setCoBorrowers(finalCoBorrowers);
            setGuarantors(finalGuarantors);
            setPartners(finalPartners);
            
            // LAN details logic remains the same
            const initialAdditionalLans = (selectedCase.additional_lan_details && selectedCase.additional_lan_details.length > 0)
                ? selectedCase.additional_lan_details.map(lan => ({
                    lan_number: lan.lan_number || '',
                    loan_amount: lan.loan_amount || '',
                    claim_amount: lan.claim_amount || '',
                    roi_percentage: lan.roi_percentage || '',
                    npa_date: formatDDMMYYYYToDateInput(lan.npa_date) || '',
                    sanction_date: formatDDMMYYYYToDateInput(lan.sanction_date) || '',
                    agreement_date: formatDDMMYYYYToDateInput(lan.agreement_date) || '',
                    application_date: formatDDMMYYYYToDateInput(lan.application_date) || '',
                    product_name: lan.product_name || '',
                    outstanding_date_as_on: formatDDMMYYYYToDateInput(lan.outstanding_date_as_on) || '',
                }))
                : [createEmptyLan()];

            setLanCount(Math.max(1, initialAdditionalLans.length));
            setAdditionalLans(initialAdditionalLans.slice(0, Math.max(1, initialAdditionalLans.length)));

            // Form data initialization for non-party fields remains the same
            setFormData({
                charges_as_on_date: formatDDMMYYYYToDateInput(selectedCase.outstanding_date_as_on) || "",
                agreement_date: formatDDMMYYYYToDateInput(selectedCase.agreement_date) || "",
                roi_percentage: selectedCase.roi_percentage || "",
                lrn_date: formatDDMMYYYYToDateInput(selectedCase.lrn_date) || "",
                incorpration_date: formatDDMMYYYYToDateInput(selectedCase.incorpration_date) || "",
                application_date: formatDDMMYYYYToDateInput(selectedCase.application_date) || "",
                sanction_date: formatDDMMYYYYToDateInput(selectedCase.sanction_date) || "",
                npa_date: formatDDMMYYYYToDateInput(selectedCase.npa_date) || "",
                guarantor_name: selectedCase.guarantor_name || "",
                guarantor_address: selectedCase.guarantor_address || "",
                proprietor_name: selectedCase.proprietor_name || "",
                proprietor_address: selectedCase.proprietor_address || "",
                coborrower1_name: selectedCase.coborrower1_name || "",
                coborrower1_address: selectedCase.coborrower1_address || "",
                loan_amount: selectedCase.loan_amount || "",
                claim_amount: selectedCase.claim_amount || "",
                address1: selectedCase.address1 || "",
                branch_address: selectedCase.branch_address || selectedCase.batch?.branch_address || "",
                product_name: selectedCase.product_name || "",
                customer_mobile_number: selectedCase.customer_mobile_number || "",
                email: selectedCase.email || "",
                roi_amount: selectedCase.roi_amount || "",
                demand_notice_date: selectedCase.demand_notice_date || "",
                tribunal_type: selectedCase.tribunal_type || "",
                evaluated_price: selectedCase.evaluated_price || "",
                plot_unit: selectedCase.plot_unit || "",
                description_of_security: selectedCase.description_of_security || "",
                type_of_security: selectedCase.type_of_security || "",
                asset_type: selectedCase.asset_type || "",
                borrower_type: selectedCase.borrower_type || "",
                borrower_pan: selectedCase.borrower_pan || "",
                city: selectedCase.city || "",
                state: selectedCase.state || "",
                taluka: selectedCase.taluka || "",
                bank_account_no: selectedCase.bank_account_no || "",
            });
        }
    }, [selectedCase]);

    const updateDetails = async () => {
        const coBorrowersPayload = coBorrowers.filter(p => p.name || p.address || p.email || p.mobile || p.pan);
        const guarantorsPayload = guarantors.filter(p => p.name || p.address || p.email || p.mobile || p.pan);
        const partnersPayload = partners.filter(p => p.name || p.address || p.email || p.mobile || p.pan);
        const additionalLansPayload = additionalLans.filter(lan => lan.lan_number || lan.loan_amount || lan.claim_amount);

        const legacyPartyFields = {};
        const maxParties = Math.max(coBorrowersPayload.length, guarantorsPayload.length, partnersPayload.length, 1);

        // This section keeps the legacy field updates as requested
        for (let i = 0; i < maxParties; i++) {
            const index = i + 1;
            const cb = coBorrowersPayload[i];
            
            // Co-Borrower Legacy Fields
            legacyPartyFields[`coborrower${index}_name`] = cb?.name || '';
            legacyPartyFields[`coborrower${index}_address`] = cb?.address || '';
            legacyPartyFields[`coborrower${index}_email`] = cb?.email || '';
            legacyPartyFields[`coborrower${index}_mobile`] = cb?.mobile || '';
            legacyPartyFields[`coborrower${index}_pan`] = cb?.pan || '';
            if (i === 0 && !cb) { // Ensure at least the first one is cleared if empty
                legacyPartyFields.coborrower1_name = '';
                legacyPartyFields.coborrower1_address = '';
            }

            const g = guarantorsPayload[i];
            // Guarantor Legacy Fields
            legacyPartyFields[`guarantor${index}_name`] = g?.name || '';
            legacyPartyFields[`guarantor${index}_address`] = g?.address || '';
            legacyPartyFields[`guarantor${index}_email`] = g?.email || '';
            legacyPartyFields[`guarantor${index}_mobile`] = g?.mobile || '';
            legacyPartyFields[`guarantor${index}_pan`] = g?.pan || '';
            if (i === 0 && !g) { // Ensure at least the first one is cleared if empty
                legacyPartyFields.guarantor1_name = '';
                legacyPartyFields.guarantor1_address = '';
            }


            const p = partnersPayload[i];
            // Partner/Proprietor Legacy Fields
            legacyPartyFields[`proprietor${index}_name`] = p?.name || '';
            legacyPartyFields[`proprietor${index}_address`] = p?.address || '';
            legacyPartyFields[`proprietor${index}_email`] = p?.email || '';
            legacyPartyFields[`proprietor${index}_mobile`] = p?.mobile || '';
            legacyPartyFields[`proprietor${index}_pan`] = p?.pan || '';
            if (i === 0 && !p) { // Ensure at least the first one is cleared if empty
                legacyPartyFields.proprietor1_name = '';
                legacyPartyFields.proprietor1_address = '';
            }
        }

        // Additional root-level legacy fields (for first party only)
        legacyPartyFields.guarantor_name = guarantorsPayload[0]?.name || '';
        legacyPartyFields.guarantor_address = guarantorsPayload[0]?.address || '';
        legacyPartyFields.proprietor_name = partnersPayload[0]?.name || '';
        legacyPartyFields.proprietor_address = partnersPayload[0]?.address || '';
        legacyPartyFields.coborrower1_name = coBorrowersPayload[0]?.name || '';
        legacyPartyFields.coborrower1_address = coBorrowersPayload[0]?.address || '';
        legacyPartyFields.coborrower1_address3 = coBorrowersPayload[0]?.address || '';

        try {
            const apiBody = {
                case_id: selectedCase?.case_id,
                lrn_date: formatDateForAPI(formData.lrn_date),
                npa_date: formatDateForAPI(formData.npa_date),
                outstanding_date_as_on: formatDateForAPI(formData.charges_as_on_date),
                agreement_date: formatDateForAPI(formData.agreement_date),
                roi_percentage: formData.roi_percentage,
                incorpration_date: formatDateForAPI(formData.incorpration_date),
                application_date: formatDateForAPI(formData.application_date),
                sanction_date: formatDateForAPI(formData.sanction_date),
                loan_amount: formData.loan_amount ? parseFloat(formData.loan_amount) : null,
                claim_amount: formData.claim_amount ? parseFloat(formData.claim_amount) : null,
                address1: formData.address1,
                branch_address: formData.branch_address,
                product_name: formData.product_name,
                customer_mobile_number: formData.customer_mobile_number,
                email: formData.email,
                roi_amount: formData.roi_amount ? parseFloat(formData.roi_amount) : null,
                lan_number_2: formData.lan_number_2,
                loan_amount_2: formData.loan_amount_2 ? parseFloat(formData.loan_amount_2) : null,
                claim_amount_2: formData.claim_amount_2 ? parseFloat(formData.claim_amount_2) : null,
                roi_percentage_2: formData.roi_percentage_2,
                npa_date_2: formatDateForAPI(formData.npa_date_2),
                sanction_date_2: formatDateForAPI(formData.sanction_date_2),
                agreement_date_2: formatDateForAPI(formData.agreement_date_2),
                product_name_2: formData.product_name_2,
                tribunal_type: formData.tribunal_type,
                demand_notice_date: formatDateForAPI(formData.demand_notice_date),
                evaluated_price: formData.evaluated_price ? parseFloat(formData.evaluated_price) : null,
                plot_unit: formData.plot_unit,
                description_of_security: formData.description_of_security,
                type_of_security: formData.type_of_security,
                asset_type: formData.asset_type,
                borrower_type: formData.borrower_type,
                borrower_pan: formData.borrower_pan,
                city: formData.city,
                state: formData.state,
                taluka: formData.taluka,
                bank_account_no: formData.bank_account_no,
                no_of_coborrower: coBorrowersPayload.length,
                no_of_guarantor: guarantorsPayload.length,
                no_of_partner: partnersPayload.length,
                no_of_additional_lan: additionalLansPayload.length,
                borrowers: coBorrowersPayload.map(({ mobile, pan, ...p }) => ({ 
                    ...p, 
                    contact_number: mobile, 
                    pan_number: pan, 
                    borrower_type: 'coborrower' 
                })),
                guarantors: guarantorsPayload.map(({ mobile, pan, ...p }) => ({ 
                    ...p, 
                    contact_number: mobile, 
                    pan_number: pan, 
                    borrower_type: 'guarantor' 
                })),
                partners: partnersPayload.map(({ mobile, pan, ...p }) => ({ 
                    ...p, 
                    contact_number: mobile, 
                    pan_number: pan, 
                    borrower_type: 'partner' 
                })),

                additional_lan_details: additionalLansPayload.map(lan => ({
                    ...lan,
                    loan_amount: lan.loan_amount ? parseFloat(lan.loan_amount) : null,
                    claim_amount: lan.claim_amount ? parseFloat(lan.claim_amount) : null,
                    npa_date: formatDateForAPI(lan.npa_date),
                    sanction_date: formatDateForAPI(lan.sanction_date),
                    agreement_date: formatDateForAPI(lan.agreement_date),
                    application_date: formatDateForAPI(lan.application_date),
                    outstanding_date_as_on: formatDateForAPI(lan.outstanding_date_as_on),
                })),
                ...legacyPartyFields,
            };

            const response = await updateAdditionalDetails(apiBody);
            if (response?.data) {
                showToast({ variant: "success", message: response.message || "Case updated successfully!" });
                onSuccess();
                onHide();
            } else {
                throw new Error(response?.message || "Failed to update case");
            }
        } catch (error) {
            console.error("Error updating case:", error);
            showToast({ variant: "error", message: error.message || "Failed to update case" });
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handlePartyChange = (partyType, index, field, value) => {
        const updater = partyType === 'coBorrowers' ? setCoBorrowers :
            partyType === 'guarantors' ? setGuarantors :
                setPartners;

        updater(prevParties => {
            const newParties = [...prevParties];
            if (!newParties[index]) {
                newParties[index] = createEmptyParty();
            }
            newParties[index][field] = value;
            return newParties;
        });
    };

    const handleCountChange = (partyType, newCount) => {
        newCount = parseInt(newCount, 10);
        const setCount = partyType === 'coBorrowers' ? setCoBorrowerCount :
            partyType === 'guarantors' ? setGuarantorCount :
                setPartnerCount;

        const setParties = partyType === 'coBorrowers' ? setCoBorrowers :
            partyType === 'guarantors' ? setGuarantors :
                setPartners;

        setCount(newCount);
        setParties(prevParties => {
            const newParties = [...prevParties];
            if (newCount < newParties.length) {
                return newParties.slice(0, newCount);
            }
            while (newParties.length < newCount) {
                newParties.push(createEmptyParty());
            }
            return newParties;
        });
    };

    const handleLanChange = (index, field, value) => {
        setAdditionalLans(prevLans => {
            const newLans = [...prevLans];
            if (!newLans[index]) {
                newLans[index] = createEmptyLan();
            }
            newLans[index][field] = value;
            return newLans;
        });
    };

    const handleLanCountChange = (newCount) => {
        newCount = parseInt(newCount, 10);
        setLanCount(newCount);
        setAdditionalLans(prevLans => {
            const newLans = [...prevLans];
            if (newCount < newLans.length) {
                return newLans.slice(0, newCount);
            }
            while (newLans.length < newCount) {
                newLans.push(createEmptyLan());
            }
            return newLans;
        });
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Update Additional Details</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
            <h5 className="mt-2 mb-3 text-primary">Borrower Details</h5>
                    <div className="p-3 mb-4 border rounded-3 bg-light">
                        <div className="row">
                            <Form.Group className="mt-3 col-md-6">
                                <Form.Label>Borrower Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={selectedCase?.customer_name || 'N/A'}
                                    disabled
                                    className="bg-white"
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-6">
                                <Form.Label>Borrower Type</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={formData.borrower_type}
                                    onChange={e => handleChange("borrower_type", e.target.value)}
                                >
                                    <option value="">Select Type</option>
                                    <option value="Individual">Individual</option>
                                    <option value="Proprietorship">Proprietorship</option>
                                </Form.Control>
                            </Form.Group>
                        </div>
                        <div className="row">
                            <Form.Group className="mt-3 col-md-4">
                                <Form.Label>Borrower PAN</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Borrower PAN"
                                    value={formData.borrower_pan}
                                    onChange={e => handleChange("borrower_pan", e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-4">
                                <Form.Label>Borrower Mobile</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Borrower Mobile"
                                    value={formData.customer_mobile_number}
                                    onChange={e => handleChange("customer_mobile_number", e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-4">
                                <Form.Label>Borrower Email</Form.Label>
                                <Form.Control
                                    type="email"
                                    placeholder="Enter Borrower Email"
                                    value={formData.email}
                                    onChange={e => handleChange("email", e.target.value)}
                                />
                            </Form.Group>
                        </div>
                        <div className="row">
                            <Form.Group className="mt-3 col-md-6">
                                <Form.Label>Proprietor Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Proprietor Name"
                                    value={formData.proprietor_name}
                                    onChange={e => handleChange("proprietor_name", e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-6">
                                <Form.Label>Proprietor Address</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={1}
                                    placeholder="Enter Proprietor Address"
                                    value={formData.proprietor_address}
                                    onChange={e => handleChange("proprietor_address", e.target.value)}
                                />
                            </Form.Group>
                        </div>
                        <div className="row">
                            <Form.Group className="mt-3 col-md-12">
                                <Form.Label>Address (Borrower Address 1)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={1}
                                    placeholder="Enter Borrower Address 1"
                                    value={formData.address1}
                                    onChange={e => handleChange("address1", e.target.value)}
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <h5 className="mt-5 mb-3 text-primary">Loan Details</h5>
                    <div className="p-3 mb-4 border rounded-3 bg-light">
                        <div className="row">
                            <Form.Group className="mt-3 col-md-6">
                                <Form.Label>LAN Number</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={selectedCase?.lan_number || 'N/A'}
                                    disabled
                                    className="bg-white"
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-6">
                                <Form.Label>Product Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Product Name"
                                    value={formData.product_name}
                                    onChange={e => handleChange("product_name", e.target.value)}
                                />
                            </Form.Group>
                        </div>
                        <div className="row">
                            <Form.Group className="mt-3 col-md-3">
                                <Form.Label>Loan Amount</Form.Label>
                                <Form.Control
                                    type="number"
                                    placeholder="Enter Loan Amount"
                                    value={formData.loan_amount}
                                    onChange={e => handleChange("loan_amount", e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-3">
                                <Form.Label>Claim Amount</Form.Label>
                                <Form.Control
                                    type="number"
                                    placeholder="Enter Claim Amount"
                                    value={formData.claim_amount}
                                    onChange={e => handleChange("claim_amount", e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-3">
                                <Form.Label>ROI Percentage</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter ROI Percentage"
                                    value={formData.roi_percentage}
                                    onChange={e => handleChange("roi_percentage", e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-3">
                                <Form.Label>ROI Amount</Form.Label>
                                <Form.Control
                                    type="number"
                                    placeholder="Enter ROI Amount"
                                    value={formData.roi_amount}
                                    onChange={e => handleChange("roi_amount", e.target.value)}
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <h5 className="mt-5 mb-3 text-primary">Important Dates</h5>
                    <div className="p-3 mb-4 border rounded-3 bg-light">
                        <div className="row">
                            <Form.Group className="mt-3 col-md-4">
                                <Form.Label>Sanction Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.sanction_date}
                                    onChange={e => handleChange("sanction_date", e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-4">
                                <Form.Label>Application Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.application_date}
                                    onChange={e => handleChange("application_date", e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-4">
                                <Form.Label>Agreement Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.agreement_date}
                                    onChange={e => handleChange("agreement_date", e.target.value)}
                                />
                            </Form.Group>
                        </div>
                        <div className="row">
                            <Form.Group className="mt-3 col-md-4">
                                <Form.Label>LRN Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.lrn_date}
                                    onChange={e => handleChange("lrn_date", e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-4">
                                <Form.Label>NPA Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.npa_date}
                                    onChange={e => handleChange("npa_date", e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-4">
                                <Form.Label>Charges As On Date </Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.charges_as_on_date}
                                    onChange={e => handleChange("charges_as_on_date", e.target.value)}
                                />
                            </Form.Group>
                        </div>
                        <div className="row">
                            <Form.Group className="mt-3 col-md-4">
                                <Form.Label>Demand Notice Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.demand_notice_date}
                                    onChange={e => handleChange("demand_notice_date", e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-4">
                                <Form.Label>Incorporation Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.incorpration_date}
                                    onChange={e => handleChange("incorpration_date", e.target.value)}
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <h5 className="mt-5 mb-3 text-primary">Other Details</h5>
                    <div className="p-3 mb-4 border rounded-3 bg-light">
                        <div className="row">
                            <Form.Group className="mt-3 col-md-6">
                                <Form.Label>Bank Account No</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Bank Account No"
                                    value={formData.bank_account_no}
                                    onChange={e => handleChange("bank_account_no", e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-6">
                                <Form.Label>Branch Address</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={1}
                                    placeholder="Enter Branch Address"
                                    value={formData.branch_address}
                                    onChange={e => handleChange("branch_address", e.target.value)}
                                />
                            </Form.Group>
                        </div>
                        <div className="row">
                            <Form.Group className="mt-3 col-md-4">
                                <Form.Label>Tribunal Type</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Tribunal Type"
                                    value={formData.tribunal_type}
                                    onChange={e => handleChange("tribunal_type", e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-4">
                                <Form.Label>City</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter City"
                                    value={formData.city}
                                    onChange={e => handleChange("city", e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-4">
                                <Form.Label>State</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter State"
                                    value={formData.state}
                                    onChange={e => handleChange("state", e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-4">
                                <Form.Label>Taluka</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Taluka"
                                    value={formData.taluka}
                                    onChange={e => handleChange("taluka", e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-4">
                                <Form.Label>Asset Type</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Asset Type"
                                    value={formData.asset_type}
                                    onChange={e => handleChange("asset_type", e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-4">
                                <Form.Label>Type of Security</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Type of Security"
                                    value={formData.type_of_security}
                                    onChange={e => handleChange("type_of_security", e.target.value)}
                                />
                            </Form.Group>
                        </div>
                        <div className="row">
                            <Form.Group className="mt-3 col-md-6">
                                <Form.Label>Plot / Unit</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Plot/Unit Details"
                                    value={formData.plot_unit}
                                    onChange={e => handleChange("plot_unit", e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mt-3 col-md-6">
                                <Form.Label>Evaluated Price</Form.Label>
                                <Form.Control
                                    type="number"
                                    placeholder="Enter Evaluated Price"
                                    value={formData.evaluated_price}
                                    onChange={e => handleChange("evaluated_price", e.target.value)}
                                />
                            </Form.Group>
                        </div>
                        <div className="row">
                            <Form.Group className="mt-3 col-md-12">
                                <Form.Label>Description of Security</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={1}
                                    placeholder="Enter Description of Security"
                                    value={formData.description_of_security}
                                    onChange={e => handleChange("description_of_security", e.target.value)}
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <h5 className="mt-5 mb-3 text-primary">Guarantor Details</h5>
                    <div className="p-3 mb-4 border rounded-3 bg-light">
                        <Form.Group className="mb-3">
                            <Form.Label>Number of Guarantors (Max 10)</Form.Label>
                            <Form.Control
                                as="select"
                                value={guarantorCount}
                                onChange={e => handleCountChange('guarantors', e.target.value)}
                            >
                                {Array.from({ length: 10 }, (_, i) => i + 1).map(count => (
                                    <option key={count} value={count}>{count}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>

                        {guarantors.slice(0, guarantorCount).map((party, index) => (
                            <div key={`guarantor-${index}`} className="border p-3 mb-3 bg-white rounded">
                                <h6 className="text-secondary">Guarantor {index + 1}</h6>
                                <div className="row">
                                    <Form.Group className="mt-2 col-md-6">
                                        <Form.Label>Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder={`Guarantor ${index + 1} Name`}
                                            value={party.name}
                                            onChange={e => handlePartyChange('guarantors', index, 'name', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2 col-md-6">
                                        <Form.Label>Address</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={1}
                                            placeholder={`Guarantor ${index + 1} Address`}
                                            value={party.address}
                                            onChange={e => handlePartyChange('guarantors', index, 'address', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2 col-md-4">
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            placeholder={`Guarantor ${index + 1} Email`}
                                            value={party.email}
                                            onChange={e => handlePartyChange('guarantors', index, 'email', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2 col-md-4">
                                        <Form.Label>Mobile</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder={`Guarantor ${index + 1} Mobile`}
                                            value={party.mobile}
                                            onChange={e => handlePartyChange('guarantors', index, 'mobile', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2 col-md-4">
                                        <Form.Label>PAN</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder={`Guarantor ${index + 1} PAN`}
                                            value={party.pan}
                                            onChange={e => handlePartyChange('guarantors', index, 'pan', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                        ))}
                    </div>

                    <h5 className="mt-5 mb-3 text-primary">Partner Details</h5>
                    <div className="p-3 mb-4 border rounded-3 bg-light">
                        <Form.Group className="mb-3">
                            <Form.Label>Number of Partners (Max 10)</Form.Label>
                            <Form.Control
                                as="select"
                                value={partnerCount}
                                onChange={e => handleCountChange('partners', e.target.value)}
                            >
                                {Array.from({ length: 10 }, (_, i) => i + 1).map(count => (
                                    <option key={count} value={count}>{count}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>

                        {partners.slice(0, partnerCount).map((party, index) => (
                            <div key={`partner-${index}`} className="border p-3 mb-3 bg-white rounded">
                                <h6 className="text-secondary">Partner {index + 1}</h6>
                                <div className="row">
                                    <Form.Group className="mt-2 col-md-6">
                                        <Form.Label>Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder={`Partner ${index + 1} Name`}
                                            value={party.name}
                                            onChange={e => handlePartyChange('partners', index, 'name', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2 col-md-6">
                                        <Form.Label>Address</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={1}
                                            placeholder={`Partner ${index + 1} Address`}
                                            value={party.address}
                                            onChange={e => handlePartyChange('partners', index, 'address', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2 col-md-4">
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            placeholder={`Partner ${index + 1} Email`}
                                            value={party.email}
                                            onChange={e => handlePartyChange('partners', index, 'email', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2 col-md-4">
                                        <Form.Label>Mobile</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder={`Partner ${index + 1} Mobile`}
                                            value={party.mobile}
                                            onChange={e => handlePartyChange('partners', index, 'mobile', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2 col-md-4">
                                        <Form.Label>PAN</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder={`Partner ${index + 1} PAN`}
                                            value={party.pan}
                                            onChange={e => handlePartyChange('partners', index, 'pan', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                        ))}
                    </div>

                    <h5 className="mt-5 mb-3 text-primary">Co-Borrower Details</h5>
                    <div className="p-3 mb-4 border rounded-3 bg-light">
                        <Form.Group className="mb-3">
                            <Form.Label>Number of Co-Borrowers (Max 10)</Form.Label>
                            <Form.Control
                                as="select"
                                value={coBorrowerCount}
                                onChange={e => handleCountChange('coBorrowers', e.target.value)}
                            >
                                {Array.from({ length: 10 }, (_, i) => i + 1).map(count => (
                                    <option key={count} value={count}>{count}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>

                        {coBorrowers.slice(0, coBorrowerCount).map((party, index) => (
                            <div key={`coborrower-${index}`} className="border p-3 mb-3 bg-white rounded">
                                <h6 className="text-secondary">Co-Borrower {index + 1}</h6>
                                <div className="row">
                                    <Form.Group className="mt-2 col-md-6">
                                        <Form.Label>Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder={`Co-Borrower ${index + 1} Name`}
                                            value={party.name}
                                            onChange={e => handlePartyChange('coBorrowers', index, 'name', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2 col-md-6">
                                        <Form.Label>Address</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={1}
                                            placeholder={`Co-Borrower ${index + 1} Address`}
                                            value={party.address}
                                            onChange={e => handlePartyChange('coBorrowers', index, 'address', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2 col-md-4">
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            placeholder={`Co-Borrower ${index + 1} Email`}
                                            value={party.email}
                                            onChange={e => handlePartyChange('coBorrowers', index, 'email', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2 col-md-4">
                                        <Form.Label>Mobile</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder={`Co-Borrower ${index + 1} Mobile`}
                                            value={party.mobile}
                                            onChange={e => handlePartyChange('coBorrowers', index, 'mobile', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2 col-md-4">
                                        <Form.Label>PAN</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder={`Co-Borrower ${index + 1} PAN`}
                                            value={party.pan}
                                            onChange={e => handlePartyChange('coBorrowers', index, 'pan', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                        ))}
                    </div>
                    <h5 className="mt-5 mb-3 text-primary">Additional LAN Details</h5>
                    <div className="p-3 mb-4 border rounded-3 bg-light">
                        <Form.Group className="mb-3">
                            <Form.Label>Number of Additional LANs (Max 10)</Form.Label>
                            <Form.Control
                                as="select"
                                value={lanCount}
                                onChange={e => handleLanCountChange(e.target.value)}
                            >
                                {Array.from({ length: 10 }, (_, i) => i + 1).map(count => (
                                    <option key={count} value={count}>{count}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>

                        {additionalLans.slice(0, lanCount).map((lan, index) => (
                            <div key={`lan-${index}`} className="border p-3 mb-3 bg-white rounded">
                                <h6 className="text-secondary">Additional LAN {index + 1}</h6>
                                <div className="row">
                                    <Form.Group className="mt-2 col-md-4">
                                        <Form.Label>LAN Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="LAN Number"
                                            value={lan.lan_number}
                                            onChange={e => handleLanChange(index, 'lan_number', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2 col-md-4">
                                        <Form.Label>Loan Amount</Form.Label>
                                        <Form.Control
                                            type="number"
                                            placeholder="Loan Amount"
                                            value={lan.loan_amount}
                                            onChange={e => handleLanChange(index, 'loan_amount', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2 col-md-4">
                                        <Form.Label>Claim Amount</Form.Label>
                                        <Form.Control
                                            type="number"
                                            placeholder="Claim Amount"
                                            value={lan.claim_amount}
                                            onChange={e => handleLanChange(index, 'claim_amount', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2 col-md-4">
                                        <Form.Label>ROI Percentage</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="ROI %"
                                            value={lan.roi_percentage}
                                            onChange={e => handleLanChange(index, 'roi_percentage', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2 col-md-4">
                                        <Form.Label>Product Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Product Name"
                                            value={lan.product_name}
                                            onChange={e => handleLanChange(index, 'product_name', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="row">
                                    <Form.Group className="mt-2 col-md-4">
                                        <Form.Label>NPA Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={lan.npa_date}
                                            onChange={e => handleLanChange(index, 'npa_date', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2 col-md-4">
                                        <Form.Label>Sanction Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={lan.sanction_date}
                                            onChange={e => handleLanChange(index, 'sanction_date', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2 col-md-4">
                                        <Form.Label>Agreement Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={lan.agreement_date}
                                            onChange={e => handleLanChange(index, 'agreement_date', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="row">
                                    <Form.Group className="mt-2 col-md-4">
                                        <Form.Label>Application Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={lan.application_date}
                                            onChange={e => handleLanChange(index, 'application_date', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2 col-md-4">
                                        <Form.Label>Outstanding Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={lan.outstanding_date_as_on}
                                            onChange={e => handleLanChange(index, 'outstanding_date_as_on', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                        ))}
                    </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Close
                </Button>
                <Button variant="primary" onClick={updateDetails}>
                    Save Changes
                </Button>
            </Modal.Footer>
        </Modal>
    );
}